// client\src\pages\MyLearning\Ongoing.js
import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Button, Chip, LinearProgress, CircularProgress } from '@mui/material';
import { getOngoingCourses } from '../../services/myLearningService';
import { formatDate } from '../../utils/dateUtils';

function LinearProgressWithLabel({ value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.8rem' }}>
            <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                    flexGrow: 1,
                    height: 4,
                    borderRadius: 5,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                        borderRadius: 5,
                    },
                }}
            />
            <Typography variant="body2" color="text.secondary">
                {`${Math.round(value)}%`}
            </Typography>
        </Box>
    );
}

function Ongoing() {
    const [ongoingCourses, setOngoingCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOngoingCourses = async () => {
            try {
                const response = await getOngoingCourses();
                if (response.success) {
                    setOngoingCourses(response.data || []);
                } else {
                    setError(response.message || "Failed to load ongoing courses.");
                }
            } catch (err) {
                setError("An error occurred while fetching ongoing courses.");
            } finally {
                setLoading(false);
            }
        };
        fetchOngoingCourses();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box textAlign="center" mt={4}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (ongoingCourses.length === 0) {
        return (
            <Box textAlign="center" mt={4}>
                <Typography variant="h6">No Ongoing Courses</Typography>
                <Typography variant="body2" color="textSecondary">
                    Your ongoing courses will appear here.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {ongoingCourses.map((course) => {
                const progress = course.progress

                return (
                    <Card
                        key={course._id}
                        sx={{
                            marginBottom: '0.5rem',
                            padding: '0.4rem',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
                            },
                            borderRadius: '8px',
                            overflow: 'hidden',
                        }}
                    >
                        <CardContent>
                            {/* Domain Chip */}
                            <Chip
                                label={course.course.domain}
                                color="primary"
                                size="small"
                                variant="outlined"
                                sx={{
                                    marginBottom: '0.7rem',
                                    fontWeight: 'bold',
                                    padding: '0 8px',
                                    borderRadius: '4px',
                                }}
                            />

                            {/* Course Title */}
                            <Typography
                                variant="h6"
                                sx={{
                                    fontSize: '1.1rem',
                                    fontWeight: '600',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {course.course.title}
                            </Typography>

                            {/* Enrollment Date */}
                            <Typography variant="body2" color="text.secondary">
                                Enrolled on: {formatDate(course.enrolledAt)}
                            </Typography>

                            {/* Progress Tracker */}
                            <LinearProgressWithLabel value={progress} />

                            {/* Action Button */}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => window.location.href = `/course/${course.course.slug}`}
                                sx={{
                                    marginTop: '1rem',
                                    textTransform: 'none',
                                    borderRadius: '16px',
                                }}
                            >
                                Continue Learning
                            </Button>
                        </CardContent>
                    </Card>
                );
            })}
        </Box>
    );
}

export default Ongoing;
